import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { UAParser } from 'ua-parser-js';
import Omnibox from '#/components/Omnibox';
import useRecentSearches from '#/shared/hooks/useRecentSearches';
import DetailViewLayout from '#/components/DetailViewLayout';
import DetailNavHeader from '#/shared/components/DetailNavHeader';
import SEO from '#/shared/components/SEO';
import useFavoriteStops from '#/transit-tracker/hooks/useFavoriteStops';
import useViewMapControls from '#/map/hooks/useViewMapControls';
import ROUTES from '#/constants/routes';
import useNavigate from '#/shared/hooks/useNavigate';
import useSelectedLocation from '#/shared/hooks/useSelectedLocation';
import { isLocationStop, getLocationStopId } from '#/shared/utils/omnibox';

function GeneralOmniboxContainer() {
  // the omnibox's input element is autofocused, on iOS this causes the top
  // of the view to scroll off the screen; this effect counteracts that.
  // background: https://stackoverflow.com/questions/56351216,
  // https://stackoverflow.com/a/60180795/2167004

  const mobileMode = useSelector(s => s.user.mobileMode);
  useEffect(() => {
    const uaParser = new UAParser();
    const { visualViewport } = window;
    const scrollWindowToTop = () => window.scrollTo(0, 0);

    // when the soft keyboard appears on iOS the visual viewport is
    // resized to accommodate it: https://stackoverflow.com/a/71547560/2167004

    if (
      uaParser.getOS().name === 'iOS' ||
      (uaParser.getOS().name === 'Mac OS' && mobileMode)
    ) {
      window?.visualViewport?.addEventListener('resize', scrollWindowToTop, {
        once: true,
      });
    }

    return () =>
      visualViewport.removeEventListener('resize', scrollWindowToTop);
  }, [mobileMode]);

  const { navigate, generateNavigateBack, generateNavigateClose } =
    useNavigate();
  const geocoderConfig = useSelector(s => s.shared.location.geocoderConfig);
  const { setSelectedLocationAndRedirect } = useSelectedLocation();
  const { addRecentSearch: addLocationSearch, recentSearches } =
    useRecentSearches();

  const { favoriteStops } = useFavoriteStops();
  const { simpleStopViewOpened, viewClosed } = useViewMapControls();

  useEffect(() => {
    simpleStopViewOpened();
    return viewClosed;
  }, [simpleStopViewOpened, viewClosed]);

  const onLocationSelected = ({ location }) => {
    if (isLocationStop(location)) {
      const stopId = getLocationStopId(location);
      navigate(ROUTES.stopById, stopId);
    } else {
      const { lat, lon: lng } = location;
      setSelectedLocationAndRedirect({ lat, lng }, locationData => {
        navigate(ROUTES.location, locationData);
      });
    }
  };

  const onFavoriteStopSelected = stop => navigate(ROUTES.stopById, stop.stopId);

  return (
    <>
      <SEO metadataId="omnibox" />
      <DetailViewLayout
        top={
          <DetailNavHeader
            onBack={generateNavigateBack()}
            onClose={generateNavigateClose()}
            content={<FormattedMessage id="omnibox.generaltitle" />}
          />
        }
        bottom={
          <Omnibox
            geocoderConfig={geocoderConfig}
            onLocationSelected={onLocationSelected}
            addLocationSearch={addLocationSearch}
            recentSearches={recentSearches}
            favoriteStops={favoriteStops}
            onFavoriteStopSelected={onFavoriteStopSelected}
            locationType="to"
            suppressNearby
          />
        }
        hasInverseBackground
      />
    </>
  );
}

export default GeneralOmniboxContainer;
